
import { StoreInfo } from "../types";

export type UnitSystem = 'imperial' | 'metric';

export interface RegionalConfig {
  country: string;
  currency: string;
  unitSystem: UnitSystem;
  massUnits: string[];
  suggestedLanguage: string;
}

const REGIONAL_MAPPING: Record<string, RegionalConfig> = {
  'US': { country: 'United States', currency: 'USD', unitSystem: 'imperial', massUnits: ['oz', 'lb'], suggestedLanguage: 'en' },
  'UK': { country: 'United Kingdom', currency: 'GBP', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'GB': { country: 'United Kingdom', currency: 'GBP', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'CA': { country: 'Canada', currency: 'CAD', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'FR': { country: 'France', currency: 'EUR', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'fr' },
  'DE': { country: 'Germany', currency: 'EUR', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'de' },
  'ES': { country: 'Spain', currency: 'EUR', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'es' },
  'IT': { country: 'Italy', currency: 'EUR', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'it' },
  'JP': { country: 'Japan', currency: 'JPY', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'AU': { country: 'Australia', currency: 'AUD', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'CH': { country: 'Switzerland', currency: 'CHF', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'de' },
  'CN': { country: 'China', currency: 'CNY', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'HK': { country: 'Hong Kong', currency: 'HKD', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'NZ': { country: 'New Zealand', currency: 'NZD', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'SE': { country: 'Sweden', currency: 'SEK', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'KR': { country: 'South Korea', currency: 'KRW', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'SG': { country: 'Singapore', currency: 'SGD', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'NO': { country: 'Norway', currency: 'NOK', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'MX': { country: 'Mexico', currency: 'MXN', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'es' },
  'IN': { country: 'India', currency: 'INR', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'BR': { country: 'Brazil', currency: 'BRL', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'ZA': { country: 'South Africa', currency: 'ZAR', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'TR': { country: 'Turkey', currency: 'TRY', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  'AE': { country: 'United Arab Emirates', currency: 'AED', unitSystem: 'metric', massUnits: ['g', 'kg'], suggestedLanguage: 'en' },
  // Default fallback
  'DEFAULT': { country: 'United States', currency: 'USD', unitSystem: 'imperial', massUnits: ['oz', 'lb'], suggestedLanguage: 'en' }
};

/**
 * Detects the country from a StoreInfo object using address parsing.
 */
export const detectCountryFromStore = (store: StoreInfo): string => {
  if (!store.address) return 'US';
  
  const address = store.address.toUpperCase();
  
  // Try to find 2-letter country codes at the end of the address
  const parts = address.split(',').map(p => p.trim());
  const lastPart = parts[parts.length - 1];
  
  if (lastPart.length === 2 && REGIONAL_MAPPING[lastPart]) {
    return lastPart;
  }
  
  // Common name checks
  if (address.includes('UNITED STATES') || address.includes(' USA')) return 'US';
  if (address.includes('UNITED KINGDOM') || address.includes(' UK')) return 'UK';
  if (address.includes('CANADA')) return 'CA';
  if (address.includes('FRANCE')) return 'FR';
  if (address.includes('GERMANY')) return 'DE';
  if (address.includes('JAPAN')) return 'JP';
  if (address.includes('AUSTRALIA')) return 'AU';
  if (address.includes('SWITZERLAND')) return 'CH';
  if (address.includes('CHINA')) return 'CN';
  if (address.includes('HONG KONG')) return 'HK';
  if (address.includes('NEW ZEALAND')) return 'NZ';
  if (address.includes('SWEDEN')) return 'SE';
  if (address.includes('KOREA')) return 'KR';
  if (address.includes('SINGAPORE')) return 'SG';
  if (address.includes('NORWAY')) return 'NO';
  if (address.includes('MEXICO')) return 'MX';
  if (address.includes('INDIA')) return 'IN';
  if (address.includes('BRAZIL')) return 'BR';
  if (address.includes('SOUTH AFRICA')) return 'ZA';
  if (address.includes('TURKEY')) return 'TR';
  if (address.includes('UNITED ARAB EMIRATES') || address.includes(' UAE')) return 'AE';
  
  return 'US'; // Fallback
};

/**
 * Returns the regional configuration based on country code.
 */
export const getRegionalConfig = (countryCode: string): RegionalConfig => {
  return REGIONAL_MAPPING[countryCode] || REGIONAL_MAPPING['DEFAULT'];
};
